import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { blogPosts } from "@/lib/blog-posts";

const Index = () => {
  const latestPosts = blogPosts.slice(0, 2);

  return (
    <div className="min-h-screen container mx-auto px-4 py-12">
      <div className="flex flex-col items-start gap-8 md:gap-12 max-w-5xl mx-auto">
        <div className="grid w-full gap-10 lg:grid-cols-[1.4fr_0.6fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4 md:space-y-6"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-left font-sans text-3xl font-semibold tracking-tight text-gray-900 dark:text-white md:text-5xl"
            >
              Kishan Vavdara
            </motion.h1>

            <p className="text-[15px] md:text-[1.02rem] text-gray-700 dark:text-gray-50">
              I'm Kishan, an <span className="font-semibold text-gray-900 dark:text-white">ML/LLM engineer</span> working on fine-tuning, evaluation, and inference optimization for large language models.
            </p>

            <p className="text-[15px] md:text-[1.02rem] text-gray-700 dark:text-gray-50">
              I'm a <span className="font-semibold text-gray-900 dark:text-white">Kaggle Competition Master and Notebook Master</span>, with multiple medal-winning solutions, including gold in competitions and gold-winning Kaggle notebooks on LLaMA, Gemma, and Qwen fine-tuning and inference. My work combines rigorous experimentation with practical systems thinking, especially where model quality, latency, and compute constraints all matter.
            </p>

            {/* <p className="text-base md:text-lg text-gray-700 dark:text-gray-50">
              I originally transitioned into ML from biotechnology through self-study and hands-on building. That path shaped how I work today: rigorous experimentation, fast iteration, and a strong bias toward practical results.
            </p> */}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="w-full max-w-lg justify-self-center pt-0 lg:max-w-[310px] lg:justify-self-end lg:pt-10"
          >
            <div className="relative mx-auto aspect-square max-w-[360px] overflow-hidden rounded-full border border-border/50 bg-background/70 shadow-sm">
              <img
                src="/images/image_prof.jpeg"
                alt="Kishan Vavdara portrait"
                className="h-full w-full scale-100 object-cover object-[34%_24%]"
              />
            </div>
          </motion.div>
        </div>
        
        {/* Additional sections */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="w-full -mt-10 md:-mt-12"
        >
          <div className="space-y-4 md:space-y-6">
            <div>
              <h2 className="text-lg md:text-xl font-semibold mb-2 text-gray-900 dark:text-white-100">What I Can Help With</h2>
              <ul className="list-disc ml-5 space-y-2 text-[15px] md:text-[1.02rem] text-gray-700 dark:text-gray-50">
                <li>Fine-tuning, post-training, and reinforcement learning workflows for LLMs using PEFT and full fine-tuning approaches.</li>
                <li>Evaluation pipelines, benchmarking, and experiment design for model selection and iteration.</li>
                <li>Inference optimization across GPU setups, including latency, throughput, batching, and memory tradeoffs.</li>
                <li>Deployment-oriented workflows for teams building with LLaMA, Gemma, Qwen, and related open models.</li>
              </ul>
            </div>

            {latestPosts.length > 0 && (
              <div className="pt-3 pb-2 md:pt-5 md:pb-4">
                <h2 className="text-lg md:text-xl font-bold mb-2 text-gray-900 dark:text-white-100">Latest Posts</h2>
                <div className="space-y-4">
                  {latestPosts.map((post) => (
                    <div
                      key={post.id}
                      className="space-y-2 rounded-2xl border border-border/50 bg-background/45 p-5 shadow-sm dark:border-white/15 dark:bg-white/[0.03]"
                    >
                      <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                        {post.date} · {post.readingTime}
                      </p>
                      <p className="text-base md:text-lg text-gray-900 dark:text-white font-semibold">
                        {post.title}
                      </p>
                      <p className="text-[14px] md:text-[0.96rem] leading-7 text-gray-700 dark:text-gray-50">
                        {post.description}
                      </p>
                      <p>
                        <Link
                          to={`/blog/${post.id}`}
                          className="font-semibold text-gray-900 underline decoration-current underline-offset-4 transition-colors hover:text-foreground dark:text-white"
                        >
                          Read full post
                        </Link>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h2 className="text-lg md:text-xl font-bold mb-2 text-gray-900 dark:text-white-100">Selected Work</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-border/50 bg-background/45 p-5 shadow-sm dark:border-white/15 dark:bg-white/[0.03]">
                  <p className="mb-1 text-sm font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                    Personal Project
                  </p>
                  <p className="mb-2 text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                    FitMyGPU
                  </p>
                  <p className="text-[14px] md:text-[0.96rem] text-gray-700 dark:text-gray-50">
                    A tool for reasoning about hardware fit, deployment constraints, and compute tradeoffs before infrastructure decisions become expensive mistakes.
                  </p>
                  <p className="mt-3">
                    <a
                      href="https://fitmygpu.com"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-gray-900 underline decoration-border underline-offset-4 transition-colors hover:text-foreground dark:text-white"
                    >
                      Visit project
                    </a>
                  </p>
                </div>

                <div className="rounded-2xl border border-border/50 bg-background/45 p-5 shadow-sm dark:border-white/15 dark:bg-white/[0.03]">
                  <p className="mb-1 text-sm font-medium uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                    Open Source
                  </p>
                  <p className="mb-2 text-base md:text-lg font-semibold text-gray-900 dark:text-white">
                    TriAttention
                  </p>
                  <p className="text-[14px] md:text-[0.96rem] text-gray-700 dark:text-gray-50">
                    Contributed GPT-OSS-120B MoE support to an upstream KV-cache compression project for long-context inference and merged it upstream.
                  </p>
                  <p className="mt-2 text-[13px] md:text-[0.9rem] text-gray-500 dark:text-gray-400">
                    Python, PyTorch, vLLM, KV cache compression
                  </p>
                  <p className="mt-3">
                    <a
                      href="https://github.com/WeianMao/triattention/commit/de4ce3907ef69782bc1c3a0015a19758da0e1e11"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-gray-900 underline decoration-border underline-offset-4 transition-colors hover:text-foreground dark:text-white"
                    >
                      Upstream merge
                    </a>
                  </p>
                </div>
              </div>
            </div>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Index;
